import axios from 'axios';
import { config } from '../config/config';
import { apiClient } from './api-client';

export interface TokenValidationResponse {
    success: boolean;
    jwt?: string;
    clientData?: {
        clientId: number;
        subdomain: string;
        clientName: string;
    };
    error?: string;
    errorType?: string;
    clientId?: number;
}

export interface OTPResponse {
    success: boolean;
    message?: string;
    error?: string;
}

export interface OTPValidationResponse {
    success: boolean;
    jwt?: string;
    clientData?: {
        subdomain: string;
        clientName: string;
    };
    error?: string;
}

/**
 * Validate portal token and get JWT
 * Supports both consent flows (with consentId/collectionPointId) and notice flows (with assignmentId)
 */
export const validateToken = async (
    token: string,
    consentId?: number,
    collectionPointId?: number,
    assignmentId?: number
): Promise<TokenValidationResponse> => {
    try {
        const payload: any = { token };

        if (consentId && collectionPointId) {
            payload.consentId = consentId;
            payload.collectionPointId = collectionPointId;
        } else if (assignmentId) {
            payload.assignmentId = assignmentId;
        }

        const response = await apiClient.post<TokenValidationResponse>(
            config.api.endpoints.portal.validateToken,
            payload
        );

        return response;
    } catch (error: any) {
        console.error('Token validation failed:', error);
        return {
            success: false,
            error: error.message || 'Token validation failed',
            errorType: error.code,
            clientId: error.data?.clientId || null  // Extract clientId from error.data
        };
    }
};

/**
 * Send OTP to email or phone
 * Supports both old format (email) and new format (identifier + identifierType)
 */
export const sendOTP = async (
    identifier: string,
    clientId: number,
    identifierType: 'EMAIL' | 'PHONE' = 'EMAIL'
): Promise<OTPResponse> => {
    try {
        // Support both old format (backward compatible) and new format
        const payload = identifierType === 'EMAIL'
            ? { email: identifier, clientId }  // Old format for backward compatibility
            : { identifier, identifierType, clientId };  // New format

        const response = await apiClient.post<OTPResponse>(
            config.api.endpoints.portal.sendOtp,
            payload
        );

        return response;
    } catch (error: any) {
        console.error('OTP sending failed:', error);
        return {
            success: false,
            error: error.message || 'OTP sending failed'
        };
    }
};

/**
 * Validate OTP and get JWT
 * Supports both old format (email) and new format (identifier + identifierType)
 */
export const validateOTP = async (
    identifier: string,
    otp: string,
    clientId: number,
    identifierType: 'EMAIL' | 'PHONE' = 'EMAIL'
): Promise<OTPValidationResponse> => {
    try {
        // Support both old format (backward compatible) and new format
        const payload = identifierType === 'EMAIL'
            ? { email: identifier, otp, clientId }  // Old format for backward compatibility
            : { identifier, identifierType, otp, clientId };  // New format

        const response = await apiClient.post<OTPValidationResponse>(
            config.api.endpoints.portal.validateOtp,
            payload
        );

        return response;
    } catch (error: any) {
        console.error('OTP validation failed:', error);
        return {
            success: false,
            error: error.message || 'OTP validation failed'
        };
    }
};

/**
 * Decode JWT token
 */
export const decodeJWT = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('JWT decode failed:', error);
        return null;
    }
};

/**
 * Check if JWT is expired
 */
export const isJWTExpired = (token: string): boolean => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
};
